import type { PropertiesPanelSectionId } from '@/lib/panels/types/properties-panel-section-id'
import { useEditorTools } from '@/contexts/editor-tools-context'
import { SelectTool } from '@/components/app/editor/tools/select-tool'
import { TypeTool } from '@/components/app/editor/tools/type-tool'
import { ShapesTool } from '@/components/app/editor/tools/shapes-tool'
import { DocumentTool } from '@/components/app/editor/tools/document-tool'
import { SpellCheckPanel } from '../../spell-check/spell-check-panel'
import type { SpellCheckBinding } from '../../spell-check/types/spell-check-binding'
import type { LeftPanelProps } from '../types/left-panel-props'

/** The Select tool's Accordion section state, owned by `LeftPanel` (`useSelectToolSections`). */
interface SelectToolSectionState {
  /** Section ids the Select tool's Accordion renders as open. */
  openSectionIds: PropertiesPanelSectionId[]
  onOpenSectionIdsChange: (next: string[]) => void
  registerSectionElement: (id: PropertiesPanelSectionId, el: HTMLDivElement | null) => void
}

type ToolContentProps = Omit<LeftPanelProps, 'className'> & {
  sections: SelectToolSectionState
  spellCheck: SpellCheckBinding
}

/** The active tool's panel content inside the Properties Panel. */
export function ToolContent({
  sections,
  spellCheck,
  textPanelRevision = 0,
  shapeManagerRef,
  isCornerResizeActive,
  onShapeCommitted,
  onBatchShapesCommitted,
  onLayerReorder,
  onUnifiedLayerArrange,
  unifiedLayerIds,
  textChains,
  onTextFramesLinked,
  selectedShapeTextParagraphs,
  isSelectedShapeTextFrame,
}: ToolContentProps) {
  const {
    activeItem,
    isNodeEditMode,
    selectedShape,
    setSelectedShape,
    selectedPenTool,
    setSelectedPenTool,
    selectedPointType,
    setSelectedPointType,
  } = useEditorTools()

  switch (activeItem) {
    case 'select':
      return (
        <SelectTool
          openSectionIds={sections.openSectionIds}
          onOpenSectionIdsChange={sections.onOpenSectionIdsChange}
          registerSectionElement={sections.registerSectionElement}
          shapeManagerRef={shapeManagerRef}
          isCornerResizeActive={isCornerResizeActive}
          textPanelRevision={textPanelRevision}
          onShapeCommitted={onShapeCommitted}
          onBatchShapesCommitted={onBatchShapesCommitted}
          onLayerReorder={onLayerReorder}
          onUnifiedLayerArrange={onUnifiedLayerArrange}
          unifiedLayerIds={unifiedLayerIds}
          textChains={textChains}
          onTextFramesLinked={onTextFramesLinked}
          selectedShapeTextParagraphs={selectedShapeTextParagraphs}
          isSelectedShapeTextFrame={isSelectedShapeTextFrame}
        />
      )

    case 'type':
      return (
        <TypeTool
          textPanelRevision={textPanelRevision}
          selectedShapeTextParagraphs={selectedShapeTextParagraphs}
          isSelectedShapeTextFrame={isSelectedShapeTextFrame}
        />
      )

    case 'shape':
      return (
        <ShapesTool
          selectedShape={selectedShape || null}
          setSelectedShape={setSelectedShape}
          selectedPenTool={selectedPenTool}
          setSelectedPenTool={setSelectedPenTool}
          selectedPointType={selectedPointType}
          setSelectedPointType={setSelectedPointType}
          isNodeEditMode={isNodeEditMode}
        />
      )

    case 'spellcheck':
      return (
        <SpellCheckPanel
          state={spellCheck.state}
          status={spellCheck.status}
          dictionaryStatus={spellCheck.dictionaryStatus}
          onStateChange={spellCheck.onStateChange}
          onAddWord={spellCheck.addWord}
          onRemoveWord={spellCheck.removeWord}
          onClearAll={spellCheck.clearWords}
          getSuggestions={spellCheck.getSuggestions}
          onFocusWord={spellCheck.onFocusWord}
          onRetry={spellCheck.onRetry}
        />
      )

    case 'document':
      return <DocumentTool />

    default:
      return null
  }
}
